import { isWarehouseWhoWereReachingSnapshot } from "@act/shared/data-transfer-objects";
import { BarChart, Bar, XAxis, YAxis, LabelList } from "recharts";
import SectionReveal from "./SectionReveal";
import SectionBanner from "./SectionBanner";
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import { border, body, muted } from "./tokens";
import { FONT_BODY, FONT_DISPLAY, TEXT_PRIMARY, TEXT_SECONDARY } from "./BrandChart";
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { useReportChartDimensions } from '../../hooks/useReportChartDimensions.hook';
import { readGapClosuresChartData } from './programGoalsChartData.util';

const GAP_CLOSURES_CHART_HEIGHT = 240;

const GAP_CLOSURES_BAR_CHART_LAYOUT = {
  margin: { top: 18, right: 4, left: 0, bottom: 8 },
  barGap: 2,
  barCategoryGap: '4%',
  barSize: 24,
  tickFontSize: 12,
  labelFontSize: 12,
} as const;

const STATUS_CONFIG = [
  { key: "inProgress", label: "In Progress", color: "var(--reema-midnight-100)" },
  { key: "scheduled", label: "Scheduled", color: "#154A74" },
  { key: "completed", label: "Completed", color: "#3E7A6B" },
  { key: "refused", label: "Refused", color: "var(--reema-coral-100)" },
] as const;

const GapClosures = ({ data }: ReportHostedSectionProps) => {
  const chartData = readGapClosuresChartData(data);
  const chartSource = isWarehouseWhoWereReachingSnapshot(data)
    ? 'warehouse'
    : 'mock';
  const { containerRef: gapClosuresChartRef, dimensions: gapClosuresChartDimensions } =
    useReportChartDimensions(GAP_CLOSURES_CHART_HEIGHT);

  return (
    <HostedReportSectionLayout
      sectionDomId="gap-closures"
      className="section--white report-hosted-section-body"
      style={{ background: "var(--white)", fontFamily: body }}
      banner={
        <SectionBanner title="Gap Closures" iconHref="#icon_relationship_engine" />
      }
    >
      <SectionReveal delay={0.1}>
        <ReportMockFieldFrame data={data} field="matrix" source={chartSource}>
        <div
          className="gap-closures-card rounded-2xl p-6 md:p-8 mb-10"
          style={{ background: "#fff", border: `1px solid ${border}` }}
        >
          {chartData.length === 0 ? (
            <p className="text-sm mb-0" style={{ color: muted }}>
              No gap-closure data for this reporting period yet. Use Refresh
              data in the builder after warehouse program-goal rows are
              available.
            </p>
          ) : (
            <>
          <div
            className="gap-closures-legend flex flex-wrap items-center gap-x-6 gap-y-2 mb-6"
            style={{ fontFamily: FONT_BODY, fontSize: 13, color: TEXT_SECONDARY }}
          >
            {STATUS_CONFIG.map((s) => (
              <div key={s.key} className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: s.color,
                  }}
                />
                <span style={{ fontWeight: 600 }}>{s.label}</span>
              </div>
            ))}
          </div>

          <div
            ref={gapClosuresChartRef}
            className="gap-closures-chart report-chart-host"
            style={{ width: "100%", minWidth: 0, height: GAP_CLOSURES_CHART_HEIGHT }}
          >
            {gapClosuresChartDimensions ? (
              <BarChart
                width={gapClosuresChartDimensions.width}
                height={gapClosuresChartDimensions.height}
                data={chartData}
                margin={GAP_CLOSURES_BAR_CHART_LAYOUT.margin}
                barGap={GAP_CLOSURES_BAR_CHART_LAYOUT.barGap}
                barCategoryGap={GAP_CLOSURES_BAR_CHART_LAYOUT.barCategoryGap}
              >
                <YAxis type="number" hide />
                <XAxis
                  dataKey="group"
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 0, right: 0 }}
                  tick={{
                    fontFamily: FONT_BODY,
                    fontSize: GAP_CLOSURES_BAR_CHART_LAYOUT.tickFontSize,
                    fontWeight: 500,
                    fill: TEXT_SECONDARY,
                  }}
                  interval={0}
                />
                {STATUS_CONFIG.map((s) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    fill={s.color}
                    radius={[4, 4, 0, 0]}
                    barSize={GAP_CLOSURES_BAR_CHART_LAYOUT.barSize}
                  >
                    <LabelList
                      dataKey={s.key}
                      position="top"
                      style={{
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 500,
                        fontSize: GAP_CLOSURES_BAR_CHART_LAYOUT.labelFontSize,
                        fill: TEXT_PRIMARY,
                      }}
                    />
                  </Bar>
                ))}
              </BarChart>
            ) : null}
          </div>
            </>
          )}
        </div>
        </ReportMockFieldFrame>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default GapClosures;
